import { CEFR_LEVELS, LEVEL_COMPLETION_THRESHOLD_PCT, type CefrLevel } from "@rmnlng/shared";
import { ArrowLeft, BookOpen, Check, Play } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useT, type I18nKey } from "@/lib/i18n";
import { useProgressLevels, type LevelProgress } from "@/lib/progress";
import { cn } from "@/lib/utils";

// Color used for grammar progress everywhere (rings, bars, dots). Kept in
// a constant so the palette match stays consistent with the Home level
// hero + Progress page's by-level strip. Not part of the CSS token set
// because it's a specific use (vocab vs. grammar distinction), not a
// theme-level accent.
const GRAMMAR_COLOR = "oklch(58% 0.12 150)";

// ══════════════════════════════════════════════════════════════════════
//  /levels — roadmap screen
// ══════════════════════════════════════════════════════════════════════
export function LevelHubPage() {
  const t = useT();
  const { data, isPending } = useProgressLevels();
  const current = data?.levels.find((l) => l.level === data.currentLevel);

  return (
    <div className="flex flex-col gap-5">
      {/* Header with back button */}
      <div className="flex items-center gap-2">
        <Link
          to="/"
          aria-label={t("common.back")}
          className="flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-[color:var(--surface-2)]"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <h1 className="text-[20px] font-semibold">{t("level_hub.title")}</h1>
      </div>

      {/* Currently-learning hero */}
      {isPending || !data || !current ? <CurrentHeroSkeleton /> : <CurrentLevelHero current={current} />}

      {/* Roadmap — all 7 levels, vertical rail */}
      <div>
        <div className="mb-2.5 px-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("level_hub.your_path")}
        </div>
        {isPending || !data ? (
          <RoadmapSkeleton />
        ) : (
          <div className="relative pl-7">
            {/* The vertical track behind the dots. `top/bottom` inset so
                the line doesn't extend past the first/last dot. */}
            <div className="absolute bottom-[18px] left-[11px] top-[18px] w-0.5 bg-border" aria-hidden />
            <div className="flex flex-col gap-2.5">
              {data.levels.map((lv) => (
                <RoadmapRow key={lv.level} row={lv} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CurrentLevelHero({ current }: { current: LevelProgress }) {
  const t = useT();
  const vocabDeficit = Math.max(
    0,
    Math.ceil((LEVEL_COMPLETION_THRESHOLD_PCT / 100) * current.vocab.total) - current.vocab.known,
  );
  const grammarDeficit = Math.max(
    0,
    Math.ceil((LEVEL_COMPLETION_THRESHOLD_PCT / 100) * current.grammar.total) - current.grammar.known,
  );
  const cardsToGo = vocabDeficit + grammarDeficit;

  return (
    <div
      className="card-elevated rounded-[var(--r-lg)] p-5"
      style={{ background: "linear-gradient(140deg, var(--accent-soft) 0%, var(--surface) 70%)" }}
    >
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[color:var(--accent)]">
        {t("level_hub.currently_learning")}
      </div>
      <div className="mb-3.5 flex items-center gap-4">
        <div
          className="flex size-14 shrink-0 items-center justify-center rounded-[14px] font-mono text-[20px] font-bold tracking-[-0.02em]"
          style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
        >
          {current.level}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[20px] font-bold tracking-[-0.02em]">{t(levelNameKey(current.level))}</div>
          <div className="mt-px text-[12px] text-text-3">
            {t("level_hub.totals", {
              words: current.vocab.total,
              rules: current.grammar.total,
            })}
          </div>
        </div>
      </div>

      <div className="mb-3.5 flex items-center gap-5">
        <Ring pct={current.vocab.pct} size={72} stroke={7} label={t("level_hub.ring_vocab")} />
        <Ring
          pct={current.grammar.pct}
          size={72}
          stroke={7}
          label={t("level_hub.ring_grammar")}
          color={GRAMMAR_COLOR}
        />
        <div className="flex-1 text-[13px] text-text-2">
          <div className="text-[12px] text-text-3">{t("level_hub.level_up_at_both")}</div>
          <div className="mt-1">
            {current.readyForExam ? (
              <span className="font-semibold text-text">{t("level_hub.ready")}</span>
            ) : (
              <>
                <span className="font-semibold text-text">{t("level_hub.cards_to_go", { n: cardsToGo })}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* CTA flips when the user is ready for the next level: instead of
          "Continue {level}" → "Final exam · {next}". Same dual-mode logic
          as the Home hero card. */}
      {(() => {
        const idx = CEFR_LEVELS.indexOf(current.level);
        const nextLevel: CefrLevel | null = idx >= 0 && idx < CEFR_LEVELS.length - 1 ? CEFR_LEVELS[idx + 1]! : null;
        const showExamCta = current.readyForExam && nextLevel !== null;
        const to = showExamCta ? `/levels/${nextLevel}/exam` : "/study?mode=words";
        const label = showExamCta
          ? t("home.cta_final_exam", { level: nextLevel })
          : t("level_hub.continue", { level: current.level });
        return (
          <Button asChild size="block" disabled={cardsToGo === 0 && !current.readyForExam}>
            <Link to={to}>
              <Play className="size-4" />
              {label}
            </Link>
          </Button>
        );
      })()}
    </div>
  );
}

function RoadmapRow({ row }: { row: LevelProgress }) {
  const t = useT();
  const isDone = row.status === "done";
  const isActive = row.status === "active";
  const isNext = row.status === "next";
  const isLocked = row.status === "locked";

  // Dot fill + icon — encodes status at a glance.
  const dotBg = isDone ? "var(--good)" : isActive ? "var(--accent)" : isNext ? "var(--surface)" : "var(--surface-3)";
  const dotBorder = isNext ? "var(--border-strong)" : "transparent";
  const dotInner = isDone ? (
    <Check className="size-3 text-white" />
  ) : isActive ? (
    <span className="size-2 rounded-full bg-white" />
  ) : (
    <span className="font-mono text-[10px] font-semibold text-text-3">{row.level}</span>
  );

  // Bars render for every row — uniform visual rhythm down the rail.
  // Done shows full bars (reinforces the "Complete" pill), locked shows
  // empty tracks (preview of what the level will demand). The single
  // exception: levels with no content at all (`total === 0` on both
  // sides) — there's nothing to show progress *toward*, so we skip the
  // bars to avoid two empty rails next to a "no content" hint.
  const hasContent = row.vocab.total > 0 || row.grammar.total > 0;

  // Row shows vocab+grammar total. Zero means "no content yet" — render
  // a subtle hint instead of "0 cards".
  const totalCards = row.vocab.total + row.grammar.total;

  return (
    <div className="relative">
      {/* Dot on the rail */}
      <div
        className="absolute left-[-28px] top-[18px] flex size-6 items-center justify-center rounded-full"
        style={{ background: dotBg, border: `2px solid ${dotBorder}` }}
      >
        {dotInner}
      </div>
      <Link
        to={`/levels/${row.level}`}
        className={cn(
          "flex flex-col rounded-[var(--r-md)] border bg-surface px-3.5 py-3 transition-colors hover:bg-[color:var(--surface-2)]",
          isActive ? "border-[color:var(--accent)]" : "border-border",
          isLocked && "opacity-55",
        )}
      >
        <div className="flex items-center gap-2.5">
          <span className="text-[15px] font-semibold">
            {row.level} · {t(levelNameKey(row.level))}
          </span>
          {isDone && (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{ background: "var(--good-soft)", color: "var(--good)" }}
            >
              {t("level_hub.pill_complete")}
            </span>
          )}
          {isActive && (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              {t("level_hub.pill_active")}
            </span>
          )}
          <div className="flex-1" />
          <span className="font-mono text-[11px] text-text-3">
            {totalCards > 0 ? t("level_hub.n_cards", { n: totalCards }) : t("level_hub.no_content")}
          </span>
        </div>
        {hasContent && (
          <div className="mt-2 flex items-center gap-2">
            <ProgressBar label={t("level_hub.bar_vocab")} pct={row.vocab.pct} color="var(--accent)" />
            <ProgressBar label={t("level_hub.bar_grammar")} pct={row.grammar.pct} color={GRAMMAR_COLOR} />
          </div>
        )}
      </Link>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
//  /levels/:level — per-level focus
// ══════════════════════════════════════════════════════════════════════
export function LevelFocusPage() {
  const t = useT();
  const { level } = useParams<{ level: string }>();
  const isValid = CEFR_LEVELS.includes(level as CefrLevel);
  const { data, isPending } = useProgressLevels();

  if (!isValid) return <Navigate to="/levels" replace />;
  const typedLevel = level as CefrLevel;
  const row = data?.levels.find((l) => l.level === typedLevel);
  const isCurrent = data?.currentLevel === typedLevel;
  const nextLevelIdx = CEFR_LEVELS.indexOf(typedLevel) + 1;
  const nextLevel = nextLevelIdx < CEFR_LEVELS.length ? CEFR_LEVELS[nextLevelIdx]! : null;

  return (
    <div className="flex flex-col gap-5">
      {/* Back button + breadcrumb-style title */}
      <div className="flex items-center gap-2 border-b border-border pb-3">
        <Link
          to="/levels"
          aria-label={t("common.back")}
          className="flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-[color:var(--surface-2)]"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-3">
            {t(levelNameKey(typedLevel))}
          </div>
          <div className="text-[15px] font-semibold">{t("level_focus.title_with_lang", { level: typedLevel })}</div>
        </div>
      </div>

      {/* Dual-ring hero */}
      {isPending || !row ? (
        <FocusHeroSkeleton />
      ) : (
        <div className="card-elevated rounded-[var(--r-lg)] p-5">
          <div className="mb-3.5 flex items-center justify-center gap-6">
            <FocusRing
              label={t("level_focus.vocab")}
              known={row.vocab.known}
              total={row.vocab.total}
              pct={row.vocab.pct}
              color="var(--accent)"
            />
            <FocusRing
              label={t("level_focus.grammar")}
              known={row.grammar.known}
              total={row.grammar.total}
              pct={row.grammar.pct}
              color={GRAMMAR_COLOR}
            />
          </div>
          <div className="rounded-[var(--r-sm)] bg-[color:var(--surface-2)] px-3 py-2.5 text-center text-[12px] text-text-2">
            {row.readyForExam
              ? t("level_focus.ready_copy", { next: nextLevel ?? "" })
              : nextLevel
                ? t("level_focus.threshold_copy", { next: nextLevel })
                : t("level_focus.threshold_copy_last")}
          </div>
        </div>
      )}

      {/* Two CTAs. Both go to /study — the queue is scoped server-side
          to the user's CURRENT level (not the viewed level). Viewing a
          locked/done level's focus screen is informational only; the
          primary "continue my studies" path lives on the currently-
          learning level's row. */}
      <div className="grid grid-cols-2 gap-2.5">
        <Button asChild disabled={!isCurrent || !row || row.vocab.total === 0}>
          <Link to="/study?mode=words">
            <Play className="size-4" />
            <span className="truncate">{t("level_focus.study_cards", { level: typedLevel })}</span>
          </Link>
        </Button>
        <Button asChild variant="secondary" disabled={!row || row.grammar.total === 0}>
          <Link to={`/grammar?tag=${typedLevel}`}>
            <BookOpen className="size-4" />
            <span className="truncate">{t("level_focus.browse_grammar", { level: typedLevel })}</span>
          </Link>
        </Button>
      </div>

      {/* If viewing a non-current level, explain why study is disabled.
          Avoids the "why is this button greyed out" confusion. */}
      {row && !isCurrent && data && (
        <div className="rounded-[var(--r-md)] bg-[color:var(--surface-2)] px-3.5 py-3 text-[12px] text-text-2">
          {t("level_focus.not_current_hint", { current: data.currentLevel })}
        </div>
      )}
    </div>
  );
}

// ─── Bits ──────────────────────────────────────────────────────────────

// Small i18n-key selector for level name. All 7 keys are present in
// strings.ru.ts / strings.ro.ts; casting to I18nKey is safe because
// the set is closed.
function levelNameKey(level: CefrLevel): I18nKey {
  return `level.name_${level}` as I18nKey;
}

function Ring({
  pct,
  size,
  stroke,
  label,
  color = "var(--accent)",
}: {
  pct: number;
  size: number;
  stroke: number;
  label: string;
  color?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
        />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center font-bold tracking-[-0.02em] text-text"
        style={{ fontSize: size > 80 ? 18 : 13 }}
      >
        {pct}%
        {label && (
          <span className="mt-0.5 text-[9px] font-medium uppercase tracking-[0.08em] text-text-3">{label}</span>
        )}
      </div>
    </div>
  );
}

// Big ring variant used in LevelFocus — shows counts below.
function FocusRing({
  label,
  known,
  total,
  pct,
  color,
}: {
  label: string;
  known: number;
  total: number;
  pct: number;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <Ring pct={pct} size={96} stroke={9} label="" color={color} />
      <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">{label}</div>
      <div className="mt-0.5 font-mono text-[12px] text-text-2">
        {known} / {total}
      </div>
    </div>
  );
}

function ProgressBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  // `progress-track` is the styled container (background + overflow:hidden +
  // rounded). The legacy `progress` class never existed in our CSS — using
  // it meant the track was invisible at 0% (no background to see, only the
  // 0-width fill on top). Inline `height: 4` overrides the default 6px so
  // the strip stays compact next to the row title.
  return (
    <div className="flex-1">
      <div className="mb-0.5 flex justify-between font-mono text-[10px] text-text-3">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="progress-track" style={{ height: 4 }}>
        <div className="fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── Skeletons ─────────────────────────────────────────────────────────

function CurrentHeroSkeleton() {
  return (
    <div className="rounded-[var(--r-lg)] border border-border bg-surface p-5">
      <Skeleton className="h-3 w-28" />
      <div className="mb-3.5 mt-3 flex items-center gap-4">
        <Skeleton className="size-14 rounded-[14px]" />
        <div className="flex-1">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="mt-1.5 h-3 w-40" />
        </div>
      </div>
      <div className="mb-3.5 flex gap-5">
        <Skeleton className="size-[72px] rounded-full" />
        <Skeleton className="size-[72px] rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-1.5 h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-11 w-full rounded-[var(--r-md)]" />
    </div>
  );
}

function RoadmapSkeleton() {
  return (
    <div className="relative pl-7">
      <div className="absolute bottom-[18px] left-[11px] top-[18px] w-0.5 bg-border" />
      <div className="flex flex-col gap-2.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="relative">
            <div className="absolute left-[-28px] top-[18px] size-6 rounded-full bg-[color:var(--surface-2)]" />
            <div className="flex items-center gap-2.5 rounded-[var(--r-md)] border border-border bg-surface px-3.5 py-3">
              <Skeleton className="h-4 w-28" />
              <div className="flex-1" />
              <Skeleton className="h-3 w-14" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FocusHeroSkeleton() {
  return (
    <div className="rounded-[var(--r-lg)] border border-border bg-surface p-5">
      <div className="mb-3.5 flex items-center justify-center gap-6">
        <Skeleton className="size-[96px] rounded-full" />
        <Skeleton className="size-[96px] rounded-full" />
      </div>
      <Skeleton className="h-8 w-full rounded-[var(--r-sm)]" />
    </div>
  );
}
